import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../components/Layout';
import { salesDistributionAPI, assignmentsAPI, materialManagementAPI, companyAPI, financeAPI } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Assignments = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('enterprise_assignments');
  const [activeTab, setActiveTab] = useState('dist-channels');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [distChannels, setDistChannels] = useState([]);
  const [salesAreas, setSalesAreas] = useState([]);
  const [salesOffices, setSalesOffices] = useState([]);
  const [salesGroups, setSalesGroups] = useState([]);
  const [shippingPoints, setShippingPoints] = useState([]);
  const [plants, setPlants] = useState([]);
  const [creditControlAreas, setCreditControlAreas] = useState([]);
  const [distChannelAssignments, setDistChannelAssignments] = useState([]);
  
  const [assignments, setAssignments] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [newAssignment, setNewAssignment] = useState({ parent: '', child: '' });


  const loadData = useCallback(async () => {
    console.log('🔄 Assignments: Starting loadData...');
    setIsLoading(true);
    try {
      console.log('📡 Calling all APIs...');
      const [orgs, divs, channels, areas, offices, groups, points, plantData, creditData, dcAssignments] = await Promise.allSettled([
        salesDistributionAPI.getSalesOrganizations(),
        salesDistributionAPI.getDivisions(),
        salesDistributionAPI.getDistributionChannels(),
        salesDistributionAPI.getSalesAreas(),
        salesDistributionAPI.getSalesOffices(),
        salesDistributionAPI.getSalesGroups(),
        salesDistributionAPI.getShippingPoints(),
        materialManagementAPI.getPlants(),
        financeAPI.getCreditControlAreas(),
        assignmentsAPI.getSalesOrgDistChannels(),
      ]);
      console.log('✅ All API calls completed');
      
      // Set data for successful requests, use empty array for failed ones
      setSalesOrgs(orgs.status === 'fulfilled' ? orgs.value.data : []);
      setDivisions(divs.status === 'fulfilled' ? divs.value.data : []);
      setDistChannels(channels.status === 'fulfilled' ? channels.value.data : []);
      setSalesAreas(areas.status === 'fulfilled' ? areas.value.data : []);
      setSalesOffices(offices.status === 'fulfilled' ? offices.value.data : []);
      setSalesGroups(groups.status === 'fulfilled' ? groups.value.data : []);
      setShippingPoints(points.status === 'fulfilled' ? points.value.data : []);
      setPlants(plantData.status === 'fulfilled' ? plantData.value.data : []);
      setCreditControlAreas(creditData.status === 'fulfilled' ? creditData.value.data : []);
      setDistChannelAssignments(dcAssignments.status === 'fulfilled' ? dcAssignments.value.data : []);
      
      // Log any failures
      const apiNames = ['Sales Orgs', 'Divisions', 'Dist Channels', 'Sales Areas', 'Sales Offices', 'Sales Groups', 'Shipping Points', 'Plants', 'Credit Control Areas', 'DC Assignments'];
      const results = [orgs, divs, channels, areas, offices, groups, points, plantData, creditData, dcAssignments];
      
      const failures = results
        .map((result, index) => ({ name: apiNames[index], result }))
        .filter(item => item.result.status === 'rejected');
      
      if (failures.length > 0) {
        console.error('❌ Failed API calls:');
        failures.forEach(failure => {
          console.error(`  - ${failure.name}:`, failure.result.reason);
        });
        toast.warning(`Loaded data with ${failures.length} warning(s). Check console for details.`);
      } else {
        console.log('✅ All data loaded successfully!');
      }

      // Load assignments based on active tab
      await loadAssignments();
      setIsLoading(false);
    } catch (error) {
      console.error('❌ Critical error in loadData:', error);
      toast.error('Failed to load data: ' + error.message);
      setIsLoading(false);
    }
  // eslint-disable-next-line
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadAssignments = async () => {
    setIsLoadingAssignments(true);
    try {
      let response;
      switch (activeTab) {
        case 'dist-channels':
          response = await assignmentsAPI.getSalesOrgDistChannels();
          break;
        case 'divisions':
          response = await assignmentsAPI.getSalesOrgDivisions();
          break;
        case 'sales-offices':
          response = await assignmentsAPI.getSalesAreaOffices();
          break;
        case 'sales-groups':
          response = await assignmentsAPI.getSalesOfficeGroups();
          break;
        case 'shipping-points':
          response = await salesDistributionAPI.getShippingPointPlantAssignments({});
          break;
        case 'org-channel-plants':
          response = await salesDistributionAPI.getSalesOrgChannelPlantAssignments({});
          break;
        case 'area-credit-control':
          response = await salesDistributionAPI.getSalesAreaCreditControlAssignments({});
          break;
        default:
          response = { data: [] };
      }
      setAssignments(response.data || []);
      setSelectedRows([]);
    } catch (error) {
      console.error('Error loading assignments:', error);
      setAssignments([]);
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  const handleAddAssignment = async () => {
    if (!newAssignment.parent || !newAssignment.child) {
      toast.error('Please select both parent and child entities');
      return;
    }

    try {
      let createFn;
      let data;
      
      switch (activeTab) {
        case 'dist-channels':
          createFn = assignmentsAPI.createSalesOrgDistChannel;
          data = { sales_org_id: newAssignment.parent, dist_channel_id: newAssignment.child };
          break;
        case 'divisions':
          createFn = assignmentsAPI.createSalesOrgDivision;
          data = { sales_org_id: newAssignment.parent, division_id: newAssignment.child };
          break;
        case 'sales-offices':
          createFn = assignmentsAPI.createSalesAreaOffice;
          data = { sales_area_id: newAssignment.parent, sales_office_id: newAssignment.child };
          break;
        case 'sales-groups':
          createFn = assignmentsAPI.createSalesOfficeGroup;
          data = { sales_office_id: newAssignment.parent, sales_group_id: newAssignment.child };
          break;
        case 'shipping-points':
          createFn = salesDistributionAPI.createShippingPointPlantAssignment;
          data = { shipping_point_id: newAssignment.parent, plant_id: newAssignment.child };
          break;
        case 'org-channel-plants':
          createFn = salesDistributionAPI.createSalesOrgChannelPlantAssignment;
          data = { 
            sales_org_id: newAssignment.parent.split('|')[0],
            distribution_channel_id: newAssignment.parent.split('|')[1],
            plant_id: newAssignment.child 
          };
          break;
        case 'area-credit-control':
          createFn = salesDistributionAPI.createSalesAreaCreditControlAssignment;
          data = { 
            sales_org_id: newAssignment.parent.split('|')[0],
            distribution_channel_id: newAssignment.parent.split('|')[1],
            division_id: newAssignment.parent.split('|')[2],
            credit_control_area_id: newAssignment.child 
          };
          break;
        default:
          return;
      }

      await createFn(data);
      toast.success('Assignment created successfully');
      setNewAssignment({ parent: '', child: '' });
      await loadAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select assignments to delete');
      return;
    }

    if (!window.confirm(`Delete ${selectedRows.length} assignment(s)?`)) {
      return;
    }

    try {
      let deleteFn;
      
      switch (activeTab) {
        case 'dist-channels':
          deleteFn = assignmentsAPI.deleteSalesOrgDistChannel;
          await Promise.all(selectedRows.map(id => deleteFn(id)));
          break;
        case 'divisions':
          deleteFn = assignmentsAPI.deleteSalesOrgDivision;
          await Promise.all(selectedRows.map(id => deleteFn(id)));
          break;
        case 'sales-offices':
          deleteFn = assignmentsAPI.deleteSalesAreaOffice;
          await Promise.all(selectedRows.map(id => deleteFn(id)));
          break;
        case 'sales-groups':
          deleteFn = assignmentsAPI.deleteSalesOfficeGroup;
          await Promise.all(selectedRows.map(id => deleteFn(id)));
          break;
        case 'shipping-points':
          await Promise.all(selectedRows.map(assignmentId => {
            const assignment = assignments.find(a => a.assignment_id === assignmentId);
            if (assignment) {
              return salesDistributionAPI.deleteShippingPointPlantAssignment(
                assignment.shipping_point_id,
                assignment.plant_id
              );
            }
          }));
          break;
        case 'org-channel-plants':
          await Promise.all(selectedRows.map(assignmentId => {
            const assignment = assignments.find(a => a.assignment_id === assignmentId);
            if (assignment) {
              return salesDistributionAPI.deleteSalesOrgChannelPlantAssignment(
                assignment.sales_org_id,
                assignment.distribution_channel_id,
                assignment.plant_id
              );
            }
          }));
          break;
        case 'area-credit-control':
          await Promise.all(selectedRows.map(assignmentId => {
            const assignment = assignments.find(a => a.assignment_id === assignmentId);
            if (assignment) {
              return salesDistributionAPI.deleteSalesAreaCreditControlAssignment(
                assignment.sales_org_id,
                assignment.distribution_channel_id,
                assignment.division_id
              );
            }
          }));
          break;
        default:
          return;
      }

      toast.success(`Deleted ${selectedRows.length} assignment(s)`);
      await loadAssignments();
    } catch (error) {
      toast.error('Failed to delete assignments');
    }
  };

  const toggleRowSelection = (assignmentId) => {
    setSelectedRows(prev => 
      prev.includes(assignmentId) 
        ? prev.filter(id => id !== assignmentId)
        : [...prev, assignmentId]
    );
  };

  const toggleAllRows = () => {
    if (selectedRows.length === assignments.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(assignments.map(a => a.assignment_id));
    }
  };

  const getTabConfig = () => {
    switch (activeTab) {
      case 'dist-channels':
        return {
          title: 'Assign Distribution Channels to Sales Organizations',
          parentLabel: 'Sales Organization',
          childLabel: 'Distribution Channel',
          parentOptions: salesOrgs,
          childOptions: distChannels,
          parentKey: 'sales_org_id',
          childKey: 'dist_channel_id',
          getParentName: (id) => {
            if (!id) return '';
            const org = salesOrgs.find(o => o.sales_org_id === id);
            return org ? `${org.sales_org_id} - ${org.sales_org_name}` : id;
          },
          getChildName: (id) => {
            if (!id) return '';
            const dc = distChannels.find(d => d.dist_channel_id === id);
            return dc ? `${dc.dist_channel_id} - ${dc.dist_channel_name}` : id;
          }
        };
      case 'divisions':
        return {
          title: 'Assign Divisions to Sales Organizations',
          parentLabel: 'Sales Organization',
          childLabel: 'Division',
          parentOptions: salesOrgs,
          childOptions: divisions,
          parentKey: 'sales_org_id',
          childKey: 'division_id',
          getParentName: (id) => {
            if (!id) return '';
            const org = salesOrgs.find(o => o.sales_org_id === id);
            return org ? `${org.sales_org_id} - ${org.sales_org_name}` : id;
          },
          getChildName: (id) => {
            if (!id) return '';
            const div = divisions.find(d => d.division_id === id);
            return div ? `${div.division_id} - ${div.division_name}` : id;
          }
        };
      case 'sales-offices':
        return {
          title: 'Assign Sales Offices to Sales Areas',
          parentLabel: 'Sales Area',
          childLabel: 'Sales Office',
          parentOptions: salesAreas,
          childOptions: salesOffices,
          parentKey: 'sales_area_id',
          childKey: 'sales_office_id',
          getParentName: (id) => {
            if (!id) return '';
            const sa = salesAreas.find(s => s.sales_area_id === id);
            return sa ? `${sa.sales_area_id} (${sa.sales_org_id}/${sa.dist_channel_id}/${sa.division_id})` : id;
          },
          getChildName: (id) => {
            if (!id) return '';
            const so = salesOffices.find(o => o.sales_office_id === id);
            return so ? `${so.sales_office_id} - ${so.sales_office_name}` : id;
          }
        };
      case 'sales-groups':
        return {
          title: 'Assign Sales Groups to Sales Offices',
          parentLabel: 'Sales Office',
          childLabel: 'Sales Group',
          parentOptions: salesOffices,
          childOptions: salesGroups,
          parentKey: 'sales_office_id',
          childKey: 'sales_group_id',
          getParentName: (id) => {
            if (!id) return '';
            const so = salesOffices.find(o => o.sales_office_id === id);
            return so ? `${so.sales_office_id} - ${so.sales_office_name}` : id;
          },
          getChildName: (id) => {
            if (!id) return '';
            const sg = salesGroups.find(g => g.sales_group_id === id);
            return sg ? `${sg.sales_group_id} - ${sg.sales_group_name}` : id;
          }
        };
      case 'shipping-points':
        return {
          title: 'Assign Plants to Shipping Points',
          parentLabel: 'Shipping Point',
          childLabel: 'Plant',
          parentOptions: shippingPoints.filter(sp => sp.status === 'Active'),
          childOptions: plants,
          parentKey: 'shipping_point_id',
          childKey: 'plant_id',
          getParentName: (id) => {
            if (!id) return '';
            const sp = shippingPoints.find(s => s.shipping_point_id === id);
            return sp ? `${sp.shipping_point_id} - ${sp.name}` : id;
          },
          getChildName: (id) => {
            if (!id) return '';
            const plant = plants.find(p => p.plant_id === id);
            return plant ? `${plant.plant_id} - ${plant.plant_name}` : id;
          }
        };
      case 'org-channel-plants':
        // Build parent options as Sales Org + Channel combinations
        const orgChannelCombos = [];
        salesOrgs.forEach(org => {
          // Get channels assigned to this org
          const assignedChannels = distChannelAssignments
            .filter(a => a.sales_org_id === org.sales_org_id)
            .map(a => a.dist_channel_id);
          
          assignedChannels.forEach(channelId => {
            const channel = distChannels.find(c => c.dist_channel_id === channelId);
            if (channel) {
              orgChannelCombos.push({
                id: `${org.sales_org_id}|${channelId}`,
                display: `${org.sales_org_id} (${org.sales_org_name}) + ${channelId} (${channel.dist_channel_name})`
              });
            }
          });
        });
        
        return {
          title: 'Assign Plants to Sales Org + Distribution Channel',
          parentLabel: 'Sales Org + Channel',
          childLabel: 'Plant',
          parentOptions: orgChannelCombos,
          childOptions: plants,
          parentKey: 'id',
          childKey: 'plant_id',
          getParentName: (id, assignment) => {
            // If called from table rendering with assignment object, construct ID from fields
            if (!id && assignment) {
              id = `${assignment.sales_org_id}|${assignment.distribution_channel_id}`;
            }
            const combo = orgChannelCombos.find(c => c.id === id);
            return combo ? combo.display : id;
          },
          getChildName: (id) => {
            if (!id) return '';
            const plant = plants.find(p => p.plant_id === id);
            return plant ? `${plant.plant_id} - ${plant.plant_name}` : id;
          }
        };
      case 'area-credit-control':
        // Build parent options as Sales Area (Org + Channel + Division)
        const salesAreaCombos = [];
        salesAreas.forEach(area => {
          const org = salesOrgs.find(o => o.sales_org_id === area.sales_org_id);
          const channel = distChannels.find(c => c.dist_channel_id === area.dist_channel_id);
          const division = divisions.find(d => d.division_id === area.division_id);
          
          if (org && channel && division) {
            salesAreaCombos.push({
              id: `${area.sales_org_id}|${area.dist_channel_id}|${area.division_id}`,
              display: `${area.sales_area_id}: ${org.sales_org_name} + ${channel.dist_channel_name} + ${division.division_name}`
            });
          }
        });
        
        return {
          title: 'Assign Credit Control Area to Sales Area',
          parentLabel: 'Sales Area (Org + Channel + Division)',
          childLabel: 'Credit Control Area',
          parentOptions: salesAreaCombos,
          childOptions: creditControlAreas,
          parentKey: 'id',
          childKey: 'credit_control_area_id',
          childKeyInOptions: 'credit_control_area', // Field name in creditControlAreas entities
          getParentName: (id, assignment) => {
            // If called from table rendering with assignment object, construct ID from fields
            if (!id && assignment) {
              id = `${assignment.sales_org_id}|${assignment.distribution_channel_id}|${assignment.division_id}`;
            }
            const combo = salesAreaCombos.find(c => c.id === id);
            return combo ? combo.display : id;
          },
          getChildName: (id) => {
            if (!id) return '';
            const cca = creditControlAreas.find(c => c.credit_control_area === id);
            return cca ? `${cca.credit_control_area} (${cca.currency}, Limit: ${cca.credit_limit.toLocaleString()})` : id;
          }
        };
      default:
        return {};
    }
  };

  const tabs = [
    { id: 'dist-channels', label: 'Distribution Channels' },
    { id: 'divisions', label: 'Divisions' },
    { id: 'sales-offices', label: 'Sales Offices' },
    { id: 'sales-groups', label: 'Sales Groups' },
    { id: 'shipping-points', label: 'Shipping Point → Plant' },
    { id: 'org-channel-plants', label: 'Org+Channel → Plant' },
    { id: 'area-credit-control', label: 'Sales Area → Credit Control' },
  ];

  const config = getTabConfig();

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="assignments-page">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Assignment Management</h2>
          <p className="text-sm text-gray-500 mt-1">Manage hierarchical entity assignments</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-6">
          {isLoading ? (
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded animate-pulse w-1/3"></div>
              <div className="h-24 bg-gray-100 rounded animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-12 bg-gray-100 rounded animate-pulse"></div>
                <div className="h-12 bg-gray-100 rounded animate-pulse"></div>
                <div className="h-12 bg-gray-100 rounded animate-pulse"></div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-900">{config.title}</h3>
              </div>

              {/* Add New Assignment Section */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {config.parentLabel}
                </label>
                <Select value={newAssignment.parent} onValueChange={(value) => setNewAssignment({...newAssignment, parent: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder={`Select ${config.parentLabel}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {config.parentOptions?.map(option => (
                      <SelectItem key={option[config.parentKey]} value={option[config.parentKey]}>
                        {config.getParentName(option[config.parentKey])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {config.childLabel}
                </label>
                <Select value={newAssignment.child} onValueChange={(value) => setNewAssignment({...newAssignment, child: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder={`Select ${config.childLabel}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {config.childOptions?.map(option => {
                      const keyField = config.childKeyInOptions || config.childKey;
                      return (
                        <SelectItem key={option[keyField]} value={option[keyField]}>
                          {config.getChildName(option[keyField])}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {writable && (<Button onClick={handleAddAssignment} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Assignment
              </Button>)}
            </div>
          </div>

          {/* Action Bar */}
          <div className="mb-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {selectedRows.length > 0 && `${selectedRows.length} row(s) selected`}
            </div>
            {writable && (<Button
              variant="destructive"
              onClick={handleDeleteSelected}
              disabled={selectedRows.length === 0}
              className="flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected
            </Button>)}
          </div>

          {/* Assignments Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 w-12">
                    <input
                      type="checkbox"
                      checked={assignments.length > 0 && selectedRows.length === assignments.length}
                      onChange={toggleAllRows}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                    {config.parentLabel}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                    {config.childLabel}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingAssignments ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                        <span>Loading assignments...</span>
                      </div>
                    </td>
                  </tr>
                ) : assignments.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8 text-gray-500">
                      No assignments found. Use the form above to create assignments.
                    </td>
                  </tr>
                ) : (
                  assignments.map((assignment) => (
                    <tr key={assignment.assignment_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(assignment.assignment_id)}
                          onChange={() => toggleRowSelection(assignment.assignment_id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="py-3 px-4 text-sm font-medium">
                        {config.getParentName(assignment[config.parentKey], assignment)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {config.getChildName(assignment[config.childKey])}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default Assignments;
